<template>
  <v-dialog
    :model-value="modelValue"
    max-width="1180px"
    persistent
    scrollable
    class="tpsv2-cmp-overlay"
    data-testid="tpsv2-compare-dialog"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card class="tpsv2-sub tpsv2-cmp">
      <div class="tpsv2-head tpsv2-head--sub bg-primary text-white">
        <div class="tpsv2-head-mark"><v-icon size="19">mdi-compare</v-icon></div>
        <div class="tpsv2-head-text">
          <h2 class="tpsv2-head-title">{{ titleText }}</h2>
          <p class="tpsv2-head-sub">
            เทียบค่าตั้งค่าของ 2 รอบบันทึก (ซ้าย = รอบเก่ากว่า) · “{{ nullLabel }}” = ไม่ได้ override
          </p>
        </div>
        <v-btn
          icon="mdi-close"
          variant="text"
          density="comfortable"
          color="white"
          aria-label="ปิดหน้าต่างเปรียบเทียบ"
          data-testid="tpsv2-compare-close-btn"
          @click="close"
        />
      </div>

      <v-card-text class="tpsv2-cmp-body">
        <v-alert
          v-if="error"
          type="error"
          variant="tonal"
          density="compact"
          class="tpsv2-alert mb-4"
          data-testid="tpsv2-compare-error"
        >
          {{ error }}
        </v-alert>

        <div v-if="loading" class="tpsv2-cmp-loading" data-testid="tpsv2-compare-loading">
          <v-progress-circular indeterminate color="primary" size="42" />
          <p class="tpsv2-muted mt-3 mb-0">กำลังโหลดข้อมูล 2 รอบเพื่อเปรียบเทียบ…</p>
        </div>

        <template v-else-if="diff">
          <div class="tpsv2-cmp-bar" data-testid="tpsv2-compare-summary">
            <v-chip color="success" variant="tonal" size="small" prepend-icon="mdi-plus">
              เพิ่ม {{ diff.summary.added }}
            </v-chip>
            <v-chip color="error" variant="tonal" size="small" prepend-icon="mdi-minus">
              ลบ {{ diff.summary.removed }}
            </v-chip>
            <v-chip color="primary" variant="tonal" size="small" prepend-icon="mdi-pencil">
              แก้ {{ diff.summary.modified }}
            </v-chip>
            <v-chip variant="tonal" size="small" prepend-icon="mdi-equal">
              เหมือนเดิม {{ diff.summary.unchanged }}
            </v-chip>
            <v-spacer />
            <v-btn-toggle
              v-model="changedOnly"
              color="primary"
              density="comfortable"
              variant="outlined"
              mandatory
              data-testid="tpsv2-compare-filter"
            >
              <v-btn :value="true" size="small">แสดงเฉพาะที่เปลี่ยน</v-btn>
              <v-btn :value="false" size="small">แสดงทั้งหมด</v-btn>
            </v-btn-toggle>
          </div>

          <p v-if="!visibleFields.length" class="tpsv2-muted mb-0" data-testid="tpsv2-compare-empty">
            {{ changedOnly ? 'ทั้ง 2 รอบตั้งค่าเหมือนกันทุกช่อง' : 'ไม่มีข้อมูลให้เปรียบเทียบ' }}
          </p>

          <!-- 🔴 The prompt columns run to thousands of characters: the table scrolls INSIDE this
               box (and each cell inside itself), so the dialog itself never scrolls sideways. -->
          <div v-else class="tpsv2-cmp-tablewrap" data-testid="tpsv2-compare-table">
            <table class="tpsv2-cmp-table">
              <thead>
                <tr>
                  <th class="tpsv2-cmp-name">ฟิลด์</th>
                  <th>v{{ leftNo }}</th>
                  <th>v{{ rightNo }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="f in visibleFields"
                  :key="f.name"
                  :class="`is-${f.state}`"
                  :data-testid="`tpsv2-compare-row-${f.name}`"
                >
                  <td class="tpsv2-cmp-name">
                    <div class="tpsv2-cmp-key">{{ f.name }}</div>
                    <v-chip size="x-small" variant="tonal" :color="stateColor(f.state)">
                      {{ stateLabel(f.state) }}
                    </v-chip>
                  </td>
                  <td>
                    <div
                      class="tpsv2-cmp-val"
                      :class="{ 'is-null': f.leftIsNull, 'is-missing': f.state === 'added' }"
                    >{{ sideText(f, 'left') }}</div>
                  </td>
                  <td>
                    <div
                      class="tpsv2-cmp-val"
                      :class="{ 'is-null': f.rightIsNull, 'is-missing': f.state === 'removed' }"
                    >{{ sideText(f, 'right') }}</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </v-card-text>

      <v-divider />
      <v-card-actions class="tpsv2-foot">
        <v-spacer />
        <v-btn variant="outlined" data-testid="tpsv2-compare-close" @click="close">
          <v-icon start>mdi-close</v-icon>
          ปิด
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * Compare two save-history snapshots of ONE Tax Planning Settings V2 version, side by side.
 *
 * The parent (the history dialog) hands over the two ticked `snapshotID`s in whatever order the
 * admin ticked them; this dialog fetches both payloads in parallel and puts the LOWER
 * `snapshotNo` on the left, so the table always reads older → newer no matter how it was opened.
 *
 * All diffing is client-side (`utils/taxSettingsSnapshotDiff.js`) — there is no compare endpoint.
 * A settings version is ONE ROW, so the diff is field-level from the start and needs no grouping:
 * the column name IS the identity. The util returns EVERY comparable column (unchanged included),
 * so the "แสดงเฉพาะที่เปลี่ยน" toggle filters on `state` here.
 *
 * 🔴 Two rendering rules the data forces:
 *   1. `null` is DATA — "no override, the runtime uses the built-in default". The util renders it
 *      as `NULL_LABEL` and flags the side with `leftIsNull`/`rightIsNull`; that side is shown
 *      italic + faded so it can never be mistaken for a value someone actually typed (an admin who
 *      cleared a textarea to `''` has NOT restored the code default, and that difference shows).
 *   2. The prompt columns are thousands of characters of multi-line text. Every cell is
 *      `pre-wrap` and scrolls inside its own box, and the table scrolls inside its wrapper — the
 *      page must never scroll horizontally.
 */
import { taxPlanningSettingsV2Api } from '@/services/taxPlanningSettingsV2Api';
import { diffSettingsSnapshots, NULL_LABEL } from '@/utils/taxSettingsSnapshotDiff';

const STATE_COLOR = {
  added: 'success',
  removed: 'error',
  modified: 'primary',
  unchanged: 'grey',
};
const STATE_LABEL = {
  added: 'เพิ่ม',
  removed: 'ลบ',
  modified: 'แก้',
  unchanged: 'เหมือนเดิม',
};

export default {
  name: 'TaxSettingsHistoryCompareV2Dialog',
  props: {
    modelValue: { type: Boolean, default: false },
    /** snapshotID of one ticked row (either side — sorted by snapshotNo on load) */
    leftSnapshotId: { type: Number, default: null },
    /** snapshotID of the other ticked row */
    rightSnapshotId: { type: Number, default: null },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      loading: false,
      error: '',
      diff: null,
      leftNo: '',
      rightNo: '',
      changedOnly: true,
      nullLabel: NULL_LABEL,
    };
  },
  computed: {
    titleText() {
      if (this.leftNo === '' || this.rightNo === '') return 'เทียบ 2 รอบบันทึก';
      return `เทียบ v${this.leftNo} ↔ v${this.rightNo}`;
    },
    /** The util hands back every column — the toggle is what narrows it to the changed ones. */
    visibleFields() {
      const list = (this.diff && Array.isArray(this.diff.fields)) ? this.diff.fields : [];
      return this.changedOnly ? list.filter((f) => f.state !== 'unchanged') : list;
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.load();
      else this.reset();
    },
  },
  methods: {
    stateColor(state) {
      return STATE_COLOR[state] || 'grey';
    },
    stateLabel(state) {
      return STATE_LABEL[state] || state;
    },
    /**
     * A side the column does not exist on at all (`added` / `removed`) is a GAP, not a value —
     * say so with a dash instead of printing the util's empty string as a blank cell.
     */
    sideText(field, side) {
      const missing = (side === 'left' && field.state === 'added')
        || (side === 'right' && field.state === 'removed');
      if (missing) return '— ไม่มีคอลัมน์นี้ในรอบนั้น';
      const raw = side === 'left' ? field.left : field.right;
      return raw === '' || raw === null || raw === undefined ? '—' : raw;
    },
    reset() {
      this.diff = null;
      this.error = '';
      this.leftNo = '';
      this.rightNo = '';
      this.changedOnly = true;
    },
    close() {
      this.$emit('update:modelValue', false);
    },
    async load() {
      this.reset();
      if (!this.leftSnapshotId || !this.rightSnapshotId) {
        this.error = 'ต้องเลือก 2 รอบเพื่อเปรียบเทียบ';
        return;
      }
      this.loading = true;
      try {
        const [a, b] = await Promise.all([
          taxPlanningSettingsV2Api.getSnapshot(this.leftSnapshotId),
          taxPlanningSettingsV2Api.getSnapshot(this.rightSnapshotId),
        ]);
        // Older round on the left, whatever order the checkboxes were ticked in.
        const older = (a.snapshotNo ?? 0) <= (b.snapshotNo ?? 0) ? a : b;
        const newer = older === a ? b : a;
        this.leftNo = older.snapshotNo;
        this.rightNo = newer.snapshotNo;
        this.diff = diffSettingsSnapshots(older.payloadJson, newer.payloadJson);
      } catch (e) {
        this.diff = null;
        this.error = (e && e.message) || 'โหลดข้อมูลเปรียบเทียบไม่สำเร็จ';
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/**
 * 🔴 The `--tps-*` ramp is declared HERE as well as in `TaxPlanningSettingsV2Dialog.vue`: this
 * dialog is teleported into its own overlay, so it is not a descendant of `.tpsv2` and would
 * otherwise resolve every var to nothing (no borders, no text colour).
 */
.tpsv2-sub {
  --tps-ink: rgba(var(--v-theme-on-surface), 0.94);
  --tps-ink-2: rgba(var(--v-theme-on-surface), 0.75);
  --tps-ink-3: rgba(var(--v-theme-on-surface), 0.66);
  --tps-line: rgba(var(--v-theme-on-surface), 0.1);
  --tps-line-soft: rgba(var(--v-theme-on-surface), 0.06);
  --tps-panel: rgba(var(--v-theme-on-surface), 0.02);
  --tps-panel-2: rgba(var(--v-theme-on-surface), 0.045);
  --tps-gutter: 20px;
  display: flex;
  flex-direction: column;
}

.tpsv2-head {
  flex: none;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px var(--tps-gutter);
}

.tpsv2-head-mark {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.16);
}

.tpsv2-head-text { flex: 1 1 auto; min-width: 0; }

/* The global reset zeroes heading weight, so titles set their own. */
.tpsv2-head-title { font-size: 1.05rem; font-weight: 600; line-height: 1.35; }
.tpsv2-head-sub { font-size: 0.75rem; line-height: 1.4; opacity: 0.82; }

.tpsv2-cmp-body { padding: 16px var(--tps-gutter); }
.tpsv2-cmp-loading { text-align: center; padding: 44px 0; }
.tpsv2-muted { font-size: 0.8rem; color: var(--tps-ink-3); }
.tpsv2-alert { font-size: 0.8rem; line-height: 1.6; }

.tpsv2-cmp-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

/**
 * The one horizontal scroller in the dialog — the page itself must never move sideways.
 * Bounded vertically as well so the header row can stay put over 85 columns of comparison
 * (`position: sticky` resolves against THIS box, so it needs a height to stick inside).
 */
.tpsv2-cmp-tablewrap {
  overflow: auto;
  max-height: 60vh;
  border: 1px solid var(--tps-line);
  border-radius: 10px;
}

.tpsv2-cmp-table {
  width: 100%;
  min-width: 760px;
  border-collapse: collapse;
  table-layout: fixed;
}

.tpsv2-cmp-table th,
.tpsv2-cmp-table td {
  vertical-align: top;
  text-align: start;
  padding: 9px 12px;
  border-bottom: 1px solid var(--tps-line-soft);
}

.tpsv2-cmp-table th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: var(--tps-panel-2);
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--tps-ink-2);
}

.tpsv2-cmp-name { width: 230px; }
.tpsv2-cmp-key {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--tps-ink);
  word-break: break-word;
  margin-bottom: 4px;
}

/* Long prompts wrap and scroll inside their own cell instead of stretching the row. */
.tpsv2-cmp-val {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.78rem;
  line-height: 1.55;
  color: var(--tps-ink);
  max-height: 220px;
  overflow: auto;
}

/* 🔴 "no override — the code default is in force" is a STATE, not a blank. */
.tpsv2-cmp-val.is-null,
.tpsv2-cmp-val.is-missing {
  font-style: italic;
  color: var(--tps-ink-3);
}

.tpsv2-cmp-table tr.is-modified { background: rgba(var(--v-theme-primary), 0.04); }
.tpsv2-cmp-table tr.is-added { background: rgba(var(--v-theme-success), 0.05); }
.tpsv2-cmp-table tr.is-removed { background: rgba(var(--v-theme-error), 0.05); }

.tpsv2-foot {
  flex: none;
  gap: 8px;
  flex-wrap: wrap;
  padding: 12px var(--tps-gutter);
}
</style>
